import { nextTick, readonly, ref, shallowRef, type Ref, type ShallowRef } from 'vue';
import type { CompiledForm } from '@record-fill';
import { GENERATED_RECORD_NOT_FOUND_MESSAGE, getGeneratedRecordForm } from '@/entities/GeneratedRecord';
import { computeSheetGeometry, type ChunkEdges } from '@/features/FormBuilder';
import { extractApiErrorMessage, isNotFoundError, useCustomToast } from '@/shared/lib';
import {
  buildSheetPdfNaming,
  compileExportSheet,
  revealClippedText,
  SHEET_EXPORT_ERROR_TITLE,
} from './record-sheet-export';
import { measureSheet } from './record-sheet-measure';
import { edgesOf, paginateSheet, type SheetBox, type SheetPage } from './record-sheet-pages';

const CAPTURE_OPTIONS = { scale: 1.5, dpr: 1, embedFonts: true, reconcile: true };

const JPEG_QUALITY = 0.8;

const A4_SHORT_SIDE_MM = 210;

const A4_LONG_SIDE_MM = 297;

// The pages were cut on the live layout; a capture that came out taller or shorter (a font that did
// not embed, for one) would put every cut in the wrong place.
const CAPTURE_HEIGHT_TOLERANCE_PX = 4;

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler a imagem'));
    reader.readAsDataURL(blob);
  });
}

// snapdom fetches an image of another origin without cookies, so the branch logo the API streams
// behind the session came back 401 and the sheet lost its logo whenever the API is another origin.
async function inlineForeignImage(image: HTMLImageElement): Promise<void> {
  const source = image.currentSrc || image.src;
  if (!/^https?:/i.test(source) || new URL(source).origin === window.location.origin) return;
  try {
    const response = await fetch(source, { credentials: 'include' });
    if (!response.ok) return;
    image.src = await readAsDataUrl(await response.blob());
  } catch {
    // snapdom still tries the image on its own.
  }
}

async function settle(stage: HTMLElement): Promise<void> {
  await document.fonts?.ready;
  const images = [...stage.querySelectorAll('img')];
  await Promise.all(images.map(inlineForeignImage));
  const pending = images.filter((image) => !image.complete);
  await Promise.all(pending.map((image) => image.decode().catch(() => undefined)));
  revealClippedText(stage);
}

interface PageComposition {
  sheet: HTMLCanvasElement;
  scale: number;
  pageHeight: number;
  pagePadding: number;
  header: SheetBox | null;
}

// Edges, never sizes, go through the one rounding, so one page ends exactly where the next begins.
function drawBand(
  context: CanvasRenderingContext2D,
  { sheet, scale }: PageComposition,
  sourceTop: number,
  sourceBottom: number,
  targetTop: number
): void {
  const top = Math.round(sourceTop * scale);
  const bottom = Math.min(Math.round(sourceBottom * scale), sheet.height);
  if (bottom <= top) return;
  const height = bottom - top;
  const targetY = Math.round(targetTop * scale);
  context.drawImage(sheet, 0, top, sheet.width, height, 0, targetY, sheet.width, height);
}

function toPageImage(composition: PageComposition, page: SheetPage): string {
  const { sheet, scale, pageHeight, pagePadding, header } = composition;
  const canvas = document.createElement('canvas');
  canvas.width = sheet.width;
  canvas.height = Math.round(pageHeight * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas indisponível');
  // JPEG has no alpha channel: a transparent pixel of the capture would come out black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  if (page.withHeader && header) {
    drawBand(context, composition, header.top, header.bottom, pagePadding);
  }
  drawBand(context, composition, page.sourceTop, page.sourceTop + page.sourceHeight, page.targetTop);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

export function useRecordSheetExport(
  getStage: () => HTMLElement | null,
  onRecordGone: () => void
): {
  isExporting: Readonly<Ref<boolean>>;
  sheetForm: Readonly<ShallowRef<CompiledForm | null>>;
  chunkEdges: Readonly<ShallowRef<ChunkEdges | null>>;
  exportRecordSheet: (recordId: number) => Promise<void>;
} {
  const isExporting = ref(false);
  const sheetForm = shallowRef<CompiledForm | null>(null);
  const chunkEdges = shallowRef<ChunkEdges | null>(null);
  const { showErrorToast } = useCustomToast();

  function reportFailure(error: unknown): void {
    const gone = isNotFoundError(error);
    const message = gone ? GENERATED_RECORD_NOT_FOUND_MESSAGE : extractApiErrorMessage(error);
    showErrorToast({ title: SHEET_EXPORT_ERROR_TITLE, type: 'error', ...(message ? { message } : {}) });
    if (gone) onRecordGone();
  }

  async function exportRecordSheet(recordId: number): Promise<void> {
    if (isExporting.value) return;
    isExporting.value = true;
    try {
      const [form, { snapdom }, { jsPDF }] = await Promise.all([
        getGeneratedRecordForm(recordId),
        import('@zumer/snapdom'),
        import('jspdf'),
      ]);
      const naming = buildSheetPdfNaming(form);
      if (!naming) throw new Error('Registro sem código');

      const compiled = compileExportSheet(form);
      sheetForm.value = compiled;
      await nextTick();
      const stage = getStage();
      if (!stage) throw new Error('Palco da folha não encontrado');
      await settle(stage);

      const width = stage.offsetWidth;
      if (width === 0) throw new Error('Folha vazia');
      const { orientation } = compiled;
      const geometry = computeSheetGeometry(width, orientation);
      const layout = measureSheet(stage);
      const pages = paginateSheet(layout, geometry);
      chunkEdges.value = edgesOf(pages, layout);
      await nextTick();

      const sheet = await snapdom.toCanvas(stage, CAPTURE_OPTIONS);
      if (sheet.width === 0 || sheet.height === 0) throw new Error('Folha vazia');
      const scale = sheet.width / width;
      if (Math.abs(sheet.height - stage.offsetHeight * scale) > CAPTURE_HEIGHT_TOLERANCE_PX) {
        throw new Error('Captura diferente da folha');
      }

      const composition: PageComposition = {
        sheet,
        scale,
        pageHeight: geometry.pageHeight,
        pagePadding: geometry.pagePadding,
        header: layout.header,
      };
      const [pageWidthMm, pageHeightMm] =
        orientation === 'landscape'
          ? [A4_LONG_SIDE_MM, A4_SHORT_SIDE_MM]
          : [A4_SHORT_SIDE_MM, A4_LONG_SIDE_MM];
      const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4', compress: true });
      pages.forEach((page, index) => {
        if (index > 0) doc.addPage();
        doc.addImage(toPageImage(composition, page), 'JPEG', 0, 0, pageWidthMm, pageHeightMm);
      });
      doc.setProperties({ title: naming.title });
      doc.save(naming.fileName);
    } catch (error) {
      reportFailure(error);
    } finally {
      chunkEdges.value = null;
      sheetForm.value = null;
      isExporting.value = false;
    }
  }

  return { isExporting: readonly(isExporting), sheetForm, chunkEdges, exportRecordSheet };
}
